import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Icon from "@mui/material/Icon";
import EducationFields from "../FormComponents/EducationFields";
import {
  fetchEducationDetailsByParent,
  addEducationData,
  editEducationData,
  deleteEducationData,
} from "../../services/educationService";

// Helper function to decode HTML entities
const decodeHTMLEntities = (text) => {
  if (!text) return text;
  const textArea = document.createElement("textarea");
  textArea.innerHTML = text;
  return textArea.value;
};

/**
 * Add Education Dialog Component
 */
export const AddEducationDialog = ({
  open,
  onClose,
  sessionId,
  userId,
  onSuccess,
  onError,
  onEducationAdded,
}) => {
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const handleClear = () => {
    setFormData({
      qualification1: "",
      others_c1: "",
      board1: "",
      course1: "",
      educationtype1: "",
      instituteschool1: "",
      institutetype1: "",
      instituecity1: "",
      institutestate1: "",
      institutecountry1: "",
      institutepincode1: "",
      startyear_c1: "",
      currentlystudying_c1: false,
      endyear_c1: "",
      grade1: "",
      degreetype1: "",
      description1: "",
      educationimage1: "",
    });
    setFieldErrors({});
  };

  const validateForm = () => {
    const errors = {};
    
    // Validate "Others" field is required when qualification is "Others"
    if (formData.qualification1 === "Others" && !formData.others_c1) {
      errors.others_c1 = "This field is required when Others is selected";
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    // Validate form before saving
    if (!validateForm()) {
      if (onError) {
        onError("Please fill in all required fields.");
      }
      return;
    }
    
    setIsLoading(true);
    try {
      const educationData = {
        qualification: formData.qualification1,
        others_c: formData.others_c1 || "",
        board: formData.board1,
        course: formData.course1,
        educationtype: formData.educationtype1,
        instituteschool: formData.instituteschool1,
        institutetype: formData.institutetype1,
        instituecity: formData.instituecity1,
        institutestate: formData.institutestate1,
        institutecountry: formData.institutecountry1,
        institutepincode: formData.institutepincode1,
        startyear_c: formData.startyear_c1,
        currentlystudying_c: formData.currentlystudying_c1 ? "1" : "0",
        endyear_c: formData.currentlystudying_c1 ? "" : formData.endyear_c1,
        grade: formData.grade1,
        degreetype: formData.degreetype1,
        description: formData.description1,
        image: formData.educationimage1,
        parentid: userId,
      };


      await addEducationData(sessionId, educationData);

      // Fetch updated list
      const updatedEducationEntries = await fetchEducationDetailsByParent(sessionId, userId);
      
      if (onEducationAdded) {
        onEducationAdded(updatedEducationEntries);
      }

      handleClear();
      onClose();
      
      if (onSuccess) {
        onSuccess("Education details added successfully!");
      }
    } catch (error) {
      if (onError) {
        onError("Failed to add education details. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = () => {
    if (!isLoading) {
      handleClear();
      onClose();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <MDBox display="flex" alignItems="center" justifyContent="space-between">
          <MDBox display="flex" alignItems="center">
            <Icon color="info" sx={{ mr: 1 }}>
              school
            </Icon>
            <MDTypography variant="h6" fontWeight="bold">
              Add New Education Details
            </MDTypography>
          </MDBox>
          <IconButton size="small" onClick={handleDialogClose} disabled={isLoading}>
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
      </DialogTitle>
      <DialogContent>
        <MDBox mt={2}>
          <EducationFields
            formData={{
              qualification1: formData.qualification1 || "",
              others_c1: formData.others_c1 || "",
              board1: formData.board1 || "",
              course1: formData.course1 || "",
              educationtype1: formData.educationtype1 || "",
              instituteschool1: formData.instituteschool1 || "",
              institutetype1: formData.institutetype1 || "",
              instituecity1: formData.instituecity1 || "",
              institutestate1: formData.institutestate1 || "",
              institutecountry1: formData.institutecountry1 || "",
              institutepincode1: formData.institutepincode1 || "",
              startyear_c1: formData.startyear_c1 || "",
              currentlystudying_c1: formData.currentlystudying_c1 || false,
              endyear_c1: formData.endyear_c1 || "",
              grade1: formData.grade1 || "",
              degreetype1: formData.degreetype1 || "",
              description1: formData.description1 || "",
              educationimage1: formData.educationimage1 || "",
            }}
            handleChange={handleChange}
            section={1}
            textInputStyle={{}}
            fieldErrors={fieldErrors}
          />
        </MDBox>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MDButton
          onClick={handleClear}
          variant="outlined"
          color="warning"
          disabled={isLoading}
        >
          Clear
        </MDButton>
        <MDButton
          onClick={handleDialogClose}
          variant="outlined"
          color="dark"
          disabled={isLoading}
        >
          Cancel
        </MDButton>
        <MDButton
          onClick={handleSave}
          variant="gradient"
          color="info"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Adding...
            </>
          ) : (
            "Add Education"
          )}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Edit Education Dialog Component
 */
export const EditEducationDialog = ({
  open,
  onClose,
  educationEntry,
  sessionId,
  userId,
  onSuccess,
  onError,
  onEducationUpdated,
}) => {
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  // Load education data when dialog opens
  React.useEffect(() => {
    if (open && educationEntry) {
      setFormData({
        qualification1: decodeHTMLEntities(educationEntry.qualification) || "",
        others_c1: decodeHTMLEntities(educationEntry.others_c) || "",
        board1: decodeHTMLEntities(educationEntry.board) || "",
        course1: decodeHTMLEntities(educationEntry.course) || "",
        educationtype1: decodeHTMLEntities(educationEntry.educationtype) || "",
        instituteschool1: decodeHTMLEntities(educationEntry.institute || educationEntry.instituteschool) || "",
        institutetype1: decodeHTMLEntities(educationEntry.institutetype) || "",
        instituecity1: decodeHTMLEntities(educationEntry.instituecity) || "",
        institutestate1: decodeHTMLEntities(educationEntry.institutestate) || "",
        institutecountry1: decodeHTMLEntities(educationEntry.institutecountry) || "",
        institutepincode1: decodeHTMLEntities(educationEntry.institutepincode) || "",
        startyear_c1: educationEntry.startyear_c || "",
        currentlystudying_c1: educationEntry.currentlystudying_c === "1" || educationEntry.currentlystudying_c === true,
        endyear_c1: educationEntry.endyear_c || "",
        grade1: decodeHTMLEntities(educationEntry.grade) || "",
        degreetype1: decodeHTMLEntities(educationEntry.degreetype) || "",
        description1: decodeHTMLEntities(educationEntry.description) || "",
        educationimage1: educationEntry.image || "",
      });
      setFieldErrors({});
    }
  }, [open, educationEntry]);

  const handleClear = () => {
    setFormData({
      qualification1: "",
      others_c1: "",
      board1: "",
      course1: "",
      educationtype1: "",
      instituteschool1: "",
      institutetype1: "",
      instituecity1: "",
      institutestate1: "",
      institutecountry1: "",
      institutepincode1: "",
      startyear_c1: "",
      currentlystudying_c1: false,
      endyear_c1: "",
      grade1: "",
      degreetype1: "",
      description1: "",
      educationimage1: "",
    });
    setFieldErrors({});
  };

  const validateForm = () => {
    const errors = {};
    
    // Validate "Others" field is required when qualification is "Others"
    if (formData.qualification1 === "Others" && !formData.others_c1) {
      errors.others_c1 = "This field is required when Others is selected";
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!educationEntry || !educationEntry.id) return;

    // Validate form before saving
    if (!validateForm()) {
      if (onError) {
        onError("Please fill in all required fields.");
      }
      return;
    }

    setIsLoading(true);
    try {
      const educationData = {
        qualification: formData.qualification1,
        others_c: formData.others_c1 || "",
        board: formData.board1,
        course: formData.course1,
        educationtype: formData.educationtype1,
        instituteschool: formData.instituteschool1,
        institutetype: formData.institutetype1,
        instituecity: formData.instituecity1,
        institutestate: formData.institutestate1,
        institutecountry: formData.institutecountry1,
        institutepincode: formData.institutepincode1,
        startyear_c: formData.startyear_c1,
        currentlystudying_c: formData.currentlystudying_c1 ? "1" : "0",
        endyear_c: formData.currentlystudying_c1 ? "" : formData.endyear_c1,
        grade: formData.grade1,
        degreetype: formData.degreetype1,
        description: formData.description1,
        image: formData.educationimage1,
        parentid: userId,
      };

      await editEducationData(sessionId, educationEntry.id, educationData);

      // Fetch updated list
      const updatedEducationEntries = await fetchEducationDetailsByParent(sessionId, userId);
      
      if (onEducationUpdated) {
        onEducationUpdated(updatedEducationEntries);
      }

      onClose();
      
      if (onSuccess) {
        onSuccess("Education details updated successfully!");
      }
    } catch (error) {
      if (onError) {
        onError("Failed to update education details. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <MDBox display="flex" alignItems="center" justifyContent="space-between">
          <MDBox display="flex" alignItems="center">
            <Icon color="primary" sx={{ mr: 1 }}>
              edit
            </Icon>
            <MDTypography variant="h6" fontWeight="bold">
              Edit Education Details
            </MDTypography>
          </MDBox>
          <IconButton size="small" onClick={handleDialogClose} disabled={isLoading}>
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
      </DialogTitle>
      <DialogContent>
        <MDBox mt={2}>
          <EducationFields
            formData={{
              qualification1: formData.qualification1 || "",
              others_c1: formData.others_c1 || "",
              board1: formData.board1 || "",
              course1: formData.course1 || "",
              educationtype1: formData.educationtype1 || "",
              instituteschool1: formData.instituteschool1 || "",
              institutetype1: formData.institutetype1 || "",
              instituecity1: formData.instituecity1 || "",
              institutestate1: formData.institutestate1 || "",
              institutecountry1: formData.institutecountry1 || "",
              institutepincode1: formData.institutepincode1 || "",
              startyear_c1: formData.startyear_c1 || "",
              currentlystudying_c1: formData.currentlystudying_c1 || false,
              endyear_c1: formData.endyear_c1 || "",
              grade1: formData.grade1 || "",
              degreetype1: formData.degreetype1 || "",
              description1: formData.description1 || "",
              educationimage1: formData.educationimage1 || "",
            }}
            handleChange={handleChange}
            section={1}
            textInputStyle={{}}
            fieldErrors={fieldErrors}
          />
        </MDBox>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MDButton
          onClick={handleClear}
          variant="outlined"
          color="warning"
          disabled={isLoading}
        >
          Clear
        </MDButton>
        <MDButton
          onClick={handleDialogClose}
          variant="outlined"
          color="dark"
          disabled={isLoading}
        >
          Cancel
        </MDButton>
        <MDButton
          onClick={handleSave}
          variant="gradient"
          color="info"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Updating...
            </>
          ) : (
            "Update"
          )}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Delete Education Confirmation Dialog Component
 */
export const DeleteEducationDialog = ({
  open,
  onClose,
  educationEntry,
  sessionId,
  userId,
  onSuccess,
  onError,
  onEducationDeleted,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    if (!educationEntry || !educationEntry.id) {
      onClose();
      return;
    }

    setIsLoading(true);
    try {
      await deleteEducationData(sessionId, educationEntry.id);

      // Fetch updated list
      const updatedEducationEntries = await fetchEducationDetailsByParent(sessionId, userId);
      
      if (onEducationDeleted) {
        onEducationDeleted(updatedEducationEntries);
      }

      onClose();
      
      if (onSuccess) {
        onSuccess("Education details deleted successfully!");
      }
    } catch (error) {
      if (onError) {
        onError("Failed to delete education details. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <MDBox display="flex" alignItems="center">
          <Icon color="error" sx={{ mr: 1 }}>
            warning
          </Icon>
          <MDTypography variant="h6" fontWeight="bold">
            Confirm Deletion
          </MDTypography>
        </MDBox>
      </DialogTitle>
      <DialogContent>
        <MDTypography variant="body2" color="text">
          Are you sure you want to delete this education detail?
        </MDTypography>
        {educationEntry && (
          <MDBox
            mt={2}
            p={2}
            sx={{ backgroundColor: "grey.100", borderRadius: 1 }}
          >
            <MDTypography variant="body2" fontWeight="medium">
              Qualification: {decodeHTMLEntities(educationEntry.qualification || educationEntry.name) || "N/A"}
            </MDTypography>
            <MDTypography variant="body2">
              Institute: {decodeHTMLEntities(educationEntry.institute) || "N/A"}
            </MDTypography>
            <MDTypography variant="body2">
              Board/University: {decodeHTMLEntities(educationEntry.board) || "N/A"}
            </MDTypography>
          </MDBox>
        )}
        <MDTypography variant="caption" color="error" mt={2} display="block">
          This action cannot be undone.
        </MDTypography>
      </DialogContent>
      <DialogActions>
        <MDButton
          onClick={handleDialogClose}
          variant="outlined"
          color="dark"
          disabled={isLoading}
        >
          Cancel
        </MDButton>
        <MDButton
          onClick={handleConfirm}
          variant="gradient"
          color="error"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Deleting...
            </>
          ) : (
            "Delete"
          )}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};
